// Paket Yükseltme Primi — saf hesaplar ve ekran modeli (server + client güvenli, yan etkisiz).
//
// Bir sipariş için tek kayıt tutulur: operatörün ilk sepet değişikliğinden önceki hâl (base) ve son hâl (new).
// Yükseltme = adet VE tutar artmış. Prim = artan tutar × oran / 100, yalnız gönderi TESLİM edilince hak
// edilir; teslim tarihine göre aylık hakedişe bağlanır (dönem kapanışı DB'de: close_upgrade_bonus_periods).
// [[opop-erpanel-prim-sistemi]]
import { isReturningShipment, istanbulClock } from '@repo/cargo'

const toNum = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : 0
}

/** Kuruş yuvarlaması (DB'deki round(x, 2) ile aynı sonucu verir). */
export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

/** Sepet toplamları: adet = Σ quantity, tutar = Σ quantity × unit_price (kargo hariç). */
export function basketTotals(lines: unknown): { qty: number; total: number } {
  const arr = Array.isArray(lines) ? lines : []
  let qty = 0
  let total = 0
  for (const line of arr as { quantity?: unknown; unit_price?: unknown }[]) {
    const q = toNum(line?.quantity)
    qty += q
    total += q * toNum(line?.unit_price)
  }
  return { qty, total: round2(total) }
}

export interface UpgradeAmounts {
  baseQty: number
  baseTotal: number
  newQty: number
  newTotal: number
}

/** Yükseltme sayılır mı: adet VE tutar artmış olmalı (yalnız fiyat artışı ya da düşürme sayılmaz). */
export function isUpgrade(u: UpgradeAmounts): boolean {
  return u.newQty > u.baseQty && u.newTotal > u.baseTotal
}

/** Artan tutar (yükseltme değilse 0). */
export function upgradeIncrease(u: UpgradeAmounts): number {
  return isUpgrade(u) ? round2(u.newTotal - u.baseTotal) : 0
}

/** Prim tutarı: artan tutar × oran (%). Negatif olmaz. */
export function bonusAmount(increase: number, ratePct: number): number {
  if (!(increase > 0) || !(ratePct > 0)) return 0
  return round2((increase * ratePct) / 100)
}

/** Prim tutarı gösterimi: "1.234,50 ₺". */
export function formatBonus(n: number): string {
  return `${n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`
}

/** Oran doğrulaması (ayarlar ekranı): 0–100 arası, en çok 2 ondalık. */
export function parseRate(input: unknown): number | null {
  if (input === null || input === undefined || input === '') return null
  const n = typeof input === 'number' ? input : Number(String(input).replace(',', '.').trim())
  if (!Number.isFinite(n) || n < 0 || n > 100) return null
  return round2(n)
}

export type UpgradeStatus = 'earned' | 'in_transit' | 'awaiting_dispatch' | 'returned' | 'cancelled'

export const UPGRADE_STATUS_LABELS: Record<UpgradeStatus, string> = {
  earned: 'Hak edildi',
  in_transit: 'Yolda',
  awaiting_dispatch: 'Kargoya verilmedi',
  returned: 'İade',
  cancelled: 'İptal',
}

export interface ClassifyInput {
  settled: boolean
  hasShipment: boolean
  durum: number | null
  statuNo: string | null
  orderCancelled: boolean
  orderDeleted: boolean
}

/**
 * Yükseltmenin prim durumu. Gönderi kaydı varsa kargo durumu belirler (teslim → hak edildi, iade süreci →
 * iade, diğer → yolda). Gönderi yoksa sipariş iptal/silinmişse iptal, değilse kargoya verilmedi.
 */
export function classifyUpgrade(i: ClassifyInput): UpgradeStatus {
  if (i.settled) return 'earned'
  if (i.orderDeleted) return 'cancelled'
  if (i.hasShipment) {
    if (i.durum === 1 || i.statuNo === '10') return 'earned'
    if (isReturningShipment({ durum: i.durum, statuNo: i.statuNo })) return 'returned'
    return 'in_transit'
  }
  return i.orderCancelled ? 'cancelled' : 'awaiting_dispatch'
}

/** upgrade_bonus_items RPC satırı. */
export interface UpgradeItemRow {
  id: string
  order_id: string
  order_number: string | null
  user_id: string | null
  user_label: string | null
  base_qty: number | string
  base_total: number | string
  new_qty: number | string
  new_total: number | string
  rate: number | string
  first_edit_at: string
  last_edit_at: string
  source: string
  period: string | null
  delivered_on: string | null
  settled_bonus: number | string | null
  payout_paid: boolean | null
  order_deleted: boolean | null
  order_status: string | null
  order_cancelled: boolean | null
  dispatched_at: string | null
  has_shipment: boolean | null
  durum: number | null
  statu_no: string | null
  teslim_tarihi: string | null
  cargo_status_text: string | null
}

export interface UpgradeItem {
  id: string
  orderId: string
  orderNumber: string
  userId: string | null
  userLabel: string
  baseQty: number
  baseTotal: number
  newQty: number
  newTotal: number
  increase: number
  rate: number
  bonus: number
  status: UpgradeStatus
  statusLabel: string
  upgradedAt: string
  /** Teslim tarihi (YYYY-MM-DD) — hak edildiyse. */
  deliveredOn: string | null
  /** Bağlandığı hakediş dönemi (YYYY-MM-01) — kapanmışsa. */
  period: string | null
  paid: boolean | null
  orderStatus: string | null
  cargoStatusText: string | null
  backfilled: boolean
}

/** E-postanın @ öncesi (ör. "arifyetrek"). */
export function shortUserLabel(label: string | null | undefined): string {
  const s = (label ?? '').trim()
  if (!s) return '—'
  const at = s.indexOf('@')
  return at > 0 ? s.slice(0, at) : s
}

/** RPC satırını ekran öğesine çevirir; yükseltme sayılmayan (düşürme, yalnız fiyat artışı) kayıtlar null. */
export function toUpgradeItem(row: UpgradeItemRow): UpgradeItem | null {
  const amounts: UpgradeAmounts = {
    baseQty: toNum(row.base_qty),
    baseTotal: toNum(row.base_total),
    newQty: toNum(row.new_qty),
    newTotal: toNum(row.new_total),
  }
  if (!isUpgrade(amounts)) return null
  const settled = !!row.period
  const status = classifyUpgrade({
    settled,
    hasShipment: !!row.has_shipment,
    durum: row.durum == null ? null : Number(row.durum),
    statuNo: row.statu_no,
    orderCancelled: !!row.order_cancelled,
    orderDeleted: !!row.order_deleted,
  })
  const increase = upgradeIncrease(amounts)
  const rate = toNum(row.rate)
  const bonus = settled && row.settled_bonus != null ? toNum(row.settled_bonus) : bonusAmount(increase, rate)
  return {
    id: row.id,
    orderId: row.order_id,
    orderNumber: row.order_number ?? '',
    userId: row.user_id,
    userLabel: shortUserLabel(row.user_label),
    ...amounts,
    increase,
    rate,
    bonus,
    status,
    statusLabel: UPGRADE_STATUS_LABELS[status],
    upgradedAt: row.first_edit_at,
    deliveredOn: row.delivered_on ?? (status === 'earned' ? row.teslim_tarihi : null),
    period: row.period,
    paid: settled ? !!row.payout_paid : null,
    orderStatus: row.order_status,
    cargoStatusText: row.cargo_status_text,
    backfilled: row.source === 'backfill',
  }
}

/** İstanbul'a göre ay anahtarı: "YYYY-MM-01". */
export function istanbulMonthKey(at: Date | string): string {
  const d = typeof at === 'string' ? new Date(at) : at
  return `${istanbulClock(d).date.slice(0, 7)}-01`
}

const MONTHS_TR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık']

/** "2026-10-01" → "Ekim 2026". */
export function monthLabel(key: string): string {
  const [y, m] = key.split('-').map(Number)
  if (!y || !m) return key
  return `${MONTHS_TR[m - 1]} ${y}`
}

export interface BonusSummary {
  /** Teslim edilmiş, henüz döneme bağlanmamış (bu ay hak edilen) prim. */
  earnedOpen: number
  earnedOpenCount: number
  /** Yoldaki yükseltmelerin olası primi (bekleyen). */
  pending: number
  pendingCount: number
  awaitingDispatchCount: number
  /** Bu ay (İstanbul) yapılan yükseltmeler ve durumları. */
  month: {
    key: string
    upgrades: number
    increase: number
    earned: number
    inTransit: number
    returned: number
    awaitingDispatch: number
    cancelled: number
  }
}

/** Ekran özeti (widget + sayfa kartları). */
export function summarizeUpgrades(items: UpgradeItem[], now: Date = new Date()): BonusSummary {
  const monthKey = istanbulMonthKey(now)
  const s: BonusSummary = {
    earnedOpen: 0,
    earnedOpenCount: 0,
    pending: 0,
    pendingCount: 0,
    awaitingDispatchCount: 0,
    month: { key: monthKey, upgrades: 0, increase: 0, earned: 0, inTransit: 0, returned: 0, awaitingDispatch: 0, cancelled: 0 },
  }
  for (const it of items) {
    if (it.status === 'earned' && !it.period) {
      s.earnedOpen += it.bonus
      s.earnedOpenCount++
    } else if (it.status === 'in_transit') {
      s.pending += it.bonus
      s.pendingCount++
    } else if (it.status === 'awaiting_dispatch') {
      s.awaitingDispatchCount++
    }
    if (istanbulMonthKey(it.upgradedAt) === monthKey) {
      s.month.upgrades++
      s.month.increase += it.increase
      if (it.status === 'earned') s.month.earned++
      else if (it.status === 'in_transit') s.month.inTransit++
      else if (it.status === 'returned') s.month.returned++
      else if (it.status === 'awaiting_dispatch') s.month.awaitingDispatch++
      else s.month.cancelled++
    }
  }
  s.earnedOpen = round2(s.earnedOpen)
  s.pending = round2(s.pending)
  s.month.increase = round2(s.month.increase)
  return s
}

export interface PayoutRow {
  id: string
  user_id: string | null
  user_label: string | null
  period: string
  upgrade_count: number
  increase_total: number | string
  bonus_total: number | string
  computed_at: string
  paid: boolean
  paid_at: string | null
  paid_by_label: string | null
}

export interface Payout {
  id: string
  userId: string | null
  userLabel: string
  period: string
  periodLabel: string
  upgradeCount: number
  increaseTotal: number
  bonusTotal: number
  computedAt: string
  paid: boolean
  paidAt: string | null
  paidByLabel: string | null
}

export function toPayout(row: PayoutRow): Payout {
  return {
    id: row.id,
    userId: row.user_id,
    userLabel: shortUserLabel(row.user_label),
    period: row.period,
    periodLabel: monthLabel(row.period),
    upgradeCount: Number(row.upgrade_count ?? 0),
    increaseTotal: toNum(row.increase_total),
    bonusTotal: toNum(row.bonus_total),
    computedAt: row.computed_at,
    paid: !!row.paid,
    paidAt: row.paid_at,
    paidByLabel: row.paid_by_label ? shortUserLabel(row.paid_by_label) : null,
  }
}

// ── Ödemeler (cari hesap) ────────────────────────────────────────────────────────────────────────────
// Primler kısmi ödenebilir (erpanel her cumartesi öder). Kalan = hak edilen (kapanmış dönemler + açık dönemde
// teslim edilen) − ödenen. Ödenen hak edileni aşabilir (avans): kalan eksi görünür, sonraki teslimler kapatır.

/** İstanbul'a göre bugün: "YYYY-MM-DD". */
export function istanbulToday(now: Date = new Date()): string {
  return istanbulClock(now).date
}

/** Ödeme tutarı tavanı (yanlış giriş emniyeti). */
export const MAX_PAYMENT_AMOUNT = 1_000_000

/**
 * Ödeme tutarı: 0'dan büyük, en çok MAX_PAYMENT_AMOUNT, kuruşa yuvarlı. "1.250,50", "1250,5", "1250.50" ve
 * "1.250" (binlik ayraç) kabul edilir.
 */
export function parsePaymentAmount(input: unknown): number | null {
  let n: number
  if (typeof input === 'number') {
    n = input
  } else if (typeof input === 'string') {
    let s = input.replace(/[\s₺]/g, '')
    if (!/^\d[\d.,]*$/.test(s)) return null
    if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.')
    else if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '')
    if (!/^\d+(\.\d+)?$/.test(s)) return null
    n = Number(s)
  } else {
    return null
  }
  if (!Number.isFinite(n)) return null
  n = round2(n)
  return n > 0 && n <= MAX_PAYMENT_AMOUNT ? n : null
}

/** Ödeme tarihi: geçerli bir "YYYY-MM-DD", bugünden (İstanbul) ileri ve 2024'ten eski değil. */
export function parsePaymentDate(input: unknown, today: string): string | null {
  if (typeof input !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(input)) return null
  const d = new Date(`${input}T00:00:00Z`)
  if (Number.isNaN(d.getTime()) || d.toISOString().slice(0, 10) !== input) return null
  if (input > today || input < '2024-01-01') return null
  return input
}

/** Ödeme notu: boşluklar sadeleşir, en çok 300 karakter; boş → null. */
export function normalizePaymentNote(input: unknown): string | null {
  if (typeof input !== 'string') return null
  const s = input.replace(/\s+/g, ' ').trim().slice(0, 300)
  return s || null
}

export interface PaymentRow {
  id: string
  user_id: string | null
  user_label: string | null
  amount: number | string
  paid_on: string
  note: string | null
  created_at: string
  created_by_label: string | null
}

export interface Payment {
  id: string
  userId: string | null
  userLabel: string
  amount: number
  /** Ödeme günü (YYYY-MM-DD). */
  paidOn: string
  note: string | null
  createdAt: string
  createdByLabel: string | null
}

export function toPayment(row: PaymentRow): Payment {
  return {
    id: row.id,
    userId: row.user_id,
    userLabel: shortUserLabel(row.user_label),
    amount: toNum(row.amount),
    paidOn: row.paid_on,
    note: row.note,
    createdAt: row.created_at,
    createdByLabel: row.created_by_label ? shortUserLabel(row.created_by_label) : null,
  }
}

export interface BonusBalance {
  userId: string | null
  userLabel: string
  /** Kesinleşmiş dönemlerin primi. */
  settled: number
  /** Açık dönemde teslim edilmiş (henüz kesinleşmemiş) prim. */
  open: number
  /** Hak edilen toplam = settled + open. */
  earned: number
  paid: number
  /** Kalan = hak edilen − ödenen; eksi → avans. */
  remaining: number
  /** Yoldaki yükseltmelerin olası primi. */
  pending: number
  lastPayment: { amount: number; paidOn: string } | null
}

const balanceKey = (userId: string | null) => userId ?? '—'

/** Operatör başına bakiye. payouts ve payments tüm zamanlar; items'tan yalnız açık dönem ve yoldakiler kullanılır. */
export function bonusBalances(input: { payouts: Payout[]; items: UpgradeItem[]; payments: Payment[] }): BonusBalance[] {
  const by = new Map<string, BonusBalance>()
  const row = (userId: string | null, label: string): BonusBalance => {
    const key = balanceKey(userId)
    let b = by.get(key)
    if (!b) {
      b = { userId, userLabel: label, settled: 0, open: 0, earned: 0, paid: 0, remaining: 0, pending: 0, lastPayment: null }
      by.set(key, b)
    }
    return b
  }
  for (const p of input.payouts) row(p.userId, p.userLabel).settled += p.bonusTotal
  for (const it of input.items) {
    if (it.status === 'earned' && !it.period) row(it.userId, it.userLabel).open += it.bonus
    else if (it.status === 'in_transit') row(it.userId, it.userLabel).pending += it.bonus
  }
  for (const pay of input.payments) {
    const b = row(pay.userId, pay.userLabel)
    b.paid += pay.amount
    if (!b.lastPayment || pay.paidOn > b.lastPayment.paidOn) b.lastPayment = { amount: pay.amount, paidOn: pay.paidOn }
  }
  for (const b of by.values()) {
    b.settled = round2(b.settled)
    b.open = round2(b.open)
    b.earned = round2(b.settled + b.open)
    b.paid = round2(b.paid)
    b.remaining = round2(b.earned - b.paid)
    b.pending = round2(b.pending)
  }
  return [...by.values()].sort((a, b) => b.remaining - a.remaining || a.userLabel.localeCompare(b.userLabel, 'tr'))
}

/** Birden çok operatörün bakiye toplamı (filtre "tümü" iken kartlar). */
export function sumBalances(balances: BonusBalance[]): Omit<BonusBalance, 'userId' | 'userLabel' | 'lastPayment'> {
  const t = { settled: 0, open: 0, earned: 0, paid: 0, remaining: 0, pending: 0 }
  for (const b of balances) {
    t.settled += b.settled
    t.open += b.open
    t.earned += b.earned
    t.paid += b.paid
    t.remaining += b.remaining
    t.pending += b.pending
  }
  return {
    settled: round2(t.settled),
    open: round2(t.open),
    earned: round2(t.earned),
    paid: round2(t.paid),
    remaining: round2(t.remaining),
    pending: round2(t.pending),
  }
}

export type CoverageState = 'paid' | 'partial' | 'unpaid'

export interface Coverage {
  covered: number
  state: CoverageState
}

/** Açık dönem satırının anahtarı (paymentCoverage sonucunda). */
export function openCoverageKey(userId: string | null): string {
  return `open:${balanceKey(userId)}`
}

/**
 * Hakediş satırlarının ödeme durumu: operatörün ödemeleri en eski kesinleşmiş dönemden başlayarak düşülür,
 * açık dönem en son karşılanır. Sonuç anahtarı: kesinleşmiş dönem → payout.id, açık dönem → openCoverageKey.
 */
export function paymentCoverage(input: { payouts: Payout[]; items: UpgradeItem[]; payments: Payment[] }): Map<string, Coverage> {
  const paid = new Map<string, number>()
  for (const pay of input.payments) paid.set(balanceKey(pay.userId), (paid.get(balanceKey(pay.userId)) ?? 0) + pay.amount)

  const open = new Map<string, { userId: string | null; bonus: number }>()
  for (const it of input.items) {
    if (it.status !== 'earned' || it.period) continue
    const o = open.get(balanceKey(it.userId)) ?? { userId: it.userId, bonus: 0 }
    o.bonus += it.bonus
    open.set(balanceKey(it.userId), o)
  }

  const out = new Map<string, Coverage>()
  const cover = (key: string, userKey: string, due: number) => {
    const left = paid.get(userKey) ?? 0
    const covered = round2(Math.min(Math.max(left, 0), Math.max(due, 0)))
    paid.set(userKey, round2(left - covered))
    const state: CoverageState = covered >= round2(due) ? 'paid' : covered > 0 ? 'partial' : 'unpaid'
    out.set(key, { covered, state })
  }
  const settled = [...input.payouts].sort((a, b) => a.period.localeCompare(b.period))
  for (const p of settled) cover(p.id, balanceKey(p.userId), p.bonusTotal)
  for (const [userKey, o] of open) cover(openCoverageKey(o.userId), userKey, round2(o.bonus))
  return out
}
